import { Outlet, useSearchParams } from 'react-router-dom';
import { NAVIGATION_CONFIG_KEY } from '@/enterprise/data/navigationArtifactSelectors';
import { useNavigationArtifact } from '@/enterprise/data/useArtifacts';
import { ENF_VERSION, EnfVersion, useEnfVersion } from '@/enterprise/features/extendedNetworkFinance/useEnfVersion';
import useFeature, { Feature } from '@/lib/features';
import NavigateWithRedirect from '@/shared/components/NavigateWithRedirect';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';

/**
 * Redirects if the feature flag is disabled.
 * Renders children if the feature flag is enabled.
 */
export const FeatureFlagProtectedRoute = ({
  children,
  featureFlag,
  redirectTo,
}: {
  featureFlag: Feature;
  children?: React.ReactNode;
  redirectTo?: string;
}) => {
  const [searchParams] = useSearchParams();
  const [enableFeatureFlag] = useFeature(featureFlag);

  if (!enableFeatureFlag) {
    let to = redirectTo ?? asEnterpriseLink('customers');

    if (searchParams) {
      to = `${to}?${new URLSearchParams(searchParams).toString()}`;
    }

    return <NavigateWithRedirect to={to} />;
  }

  return children ?? <Outlet />;
};

/**
 * Redirects if the feature flag is enabled.
 * Renders children if the feature flag is disabled.
 */
export const FeatureFlagEnabledRoute = ({
  children,
  featureFlag,
  redirectTo,
}: {
  featureFlag: Feature;
  children?: React.ReactNode;
  redirectTo?: string;
}) => {
  const [searchParams] = useSearchParams();
  const [enableFeatureFlag] = useFeature(featureFlag);

  if (enableFeatureFlag) {
    let to = redirectTo ?? asEnterpriseLink('customers');

    if (searchParams) {
      to = `${to}?${new URLSearchParams(searchParams).toString()}`;
    }

    return <NavigateWithRedirect to={to} />;
  }

  return children ?? <Outlet />;
};

/**
 * Route guard for ENF pages. Checks the version treatment, and with `requireQualifyingArtifact` also
 * requires a qualifying `EXTENDED_NETWORK_FINANCE` artifact — the treatment is global, so the
 * artifact is the only thing that narrows a route to an audience. Redirects to the homepage when the
 * treatment is 'off' or unknown, when it matches `excludeVersion`, or when a required artifact is
 * missing.
 *
 * Which versions may reach a route is the caller's call (`excludeVersion`), so a later treatment such
 * as `0.2` is allowed by default rather than needing this file changed.
 *
 * Note: the homepage route automatically redirects non-unified users to customers
 * (see Homepage component), so this works correctly for both experiences.
 */
export function EnfVersionProtectedRoute({
  children,
  excludeVersion,
  requireQualifyingArtifact = false,
}: {
  children?: React.ReactNode;
  excludeVersion?: EnfVersion;
  requireQualifyingArtifact?: boolean;
}) {
  const enfVersion = useEnfVersion();
  const { data: artifact } = useNavigationArtifact(NAVIGATION_CONFIG_KEY.EXTENDED_NETWORK_FINANCE);

  if (enfVersion === ENF_VERSION.OFF) {
    return <NavigateWithRedirect to={asEnterpriseLink('homepage')} />;
  }

  if (excludeVersion && enfVersion === excludeVersion) {
    return <NavigateWithRedirect to={asEnterpriseLink('homepage')} />;
  }

  /**
   * The artifact is the audience gate. Get a Quote's nav child comes from the client registry, so
   * there is no authored child to look for — qualifying for the config is the whole requirement.
   */
  if (requireQualifyingArtifact && !artifact) {
    return <NavigateWithRedirect to={asEnterpriseLink('homepage')} />;
  }

  return children ?? <Outlet />;
}
